import { LOCALES, type Locale } from "../schema/core.ts";

/** Shared with the landing page so a visitor's language choice carries over. */
const LOCALE_STORAGE_KEY = "bodemleven.locale";

const isLocale = (value: unknown): value is Locale =>
  typeof value === "string" && (LOCALES as readonly string[]).includes(value);

const readStoredLocale = (): Locale | null => {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
};

export const storeLocale = (locale: Locale): void => {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage can be unavailable (private mode); the choice then lasts for this visit.
  }
};

/** Set by the Worker on `<html>` from the visitor's country (NL → nl, else en). */
const readCountryLocale = (): Locale | null => {
  const hint = document.documentElement.dataset["countryLocale"];
  return isLocale(hint) ? hint : null;
};

/** Stored choice → visitor's country (Worker hint) → browser language → nl. */
export const detectLocale = (): Locale => {
  if (typeof window === "undefined") return "nl";
  const stored = readStoredLocale();
  if (stored) return stored;
  const fromCountry = readCountryLocale();
  if (fromCountry) return fromCountry;
  const language = window.navigator.language;
  if (!language) return "nl";
  return language.toLowerCase().startsWith("nl") ? "nl" : "en";
};
